'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface ProfilePageProps {
  params: { username: string };
}

interface Profile {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  bio: string;
  created_at: string;
}

interface Game {
  id: string;
  title: string;
  category: string;
  plays_count: number;
  likes_count: number;
  thumbnail_url: string | null;
  game_status: string;
}

export default function ProfilePage({ params }: ProfilePageProps) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [stats, setStats] = useState({ total: 0, published: 0, totalLikes: 0, totalPlays: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const supabase = createClient();
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('id, username, display_name, avatar_url, bio, created_at')
          .eq('username', params.username)
          .single();

        if (profileData) {
          setProfile(profileData);

          const { data: gamesData } = await supabase
            .from('games')
            .select('id, title, category, plays_count, likes_count, thumbnail_url, game_status')
            .eq('user_id', profileData.id)
            .eq('game_status', 'published')
            .order('created_at', { ascending: false });

          const allGames = gamesData || [];
          setGames(allGames);
          setStats({
            total: allGames.length,
            published: allGames.filter((g) => g.game_status === 'published').length,
            totalLikes: allGames.reduce((sum, g) => sum + (g.likes_count || 0), 0),
            totalPlays: allGames.reduce((sum, g) => sum + (g.plays_count || 0), 0),
          });
        }
      } catch {}
      setLoading(false);
    };
    fetchProfile();
  }, [params.username]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatJoinDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <AppLayout currentPath="/profile">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-32" />
            <div className="bg-card border border-border rounded-2xl p-6">
              <div className="flex gap-5">
                <div className="w-20 h-20 rounded-full bg-muted" />
                <div className="flex-1 space-y-3">
                  <div className="h-6 bg-muted rounded w-48" />
                  <div className="h-4 bg-muted rounded w-32" />
                  <div className="h-4 bg-muted rounded w-full max-w-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!profile) {
    return (
      <AppLayout currentPath="/profile">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8 text-center">
          <Icon name="UserIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Perfil não encontrado</h1>
          <p className="text-sm text-muted-foreground mb-4">O utilizador @{params.username} não existe.</p>
          <Link href="/explore" className="text-primary hover:text-primary/80 text-sm">
            Explorar jogos
          </Link>
        </div>
      </AppLayout>
    );
  }

  const displayName = profile.display_name || profile.username;
  const initials = getInitials(displayName);

  return (
    <AppLayout currentPath="/profile">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
        <Link
          href="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <Icon name="ChevronLeftIcon" size={16} />
          Voltar ao dashboard
        </Link>

        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-20 h-20 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={displayName} className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-white">{initials}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground">{displayName}</h1>
              <p className="text-sm text-muted-foreground mb-2">@{profile.username}</p>
              {profile.bio && (
                <p className="text-sm text-muted-foreground leading-relaxed max-w-lg">{profile.bio}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                <Icon name="CalendarIcon" size={12} />
                Membro desde {formatJoinDate(profile.created_at)}
              </p>
            </div>
            <Link
              href="/create-game"
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 active:scale-95 transition-all duration-150"
            >
              <Icon name="SparklesIcon" size={15} variant="solid" />
              Criar jogo
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
            {[
              { label: 'Jogos criados', value: stats.total, icon: 'RectangleStackIcon', color: 'text-primary' },
              { label: 'Publicados', value: stats.published, icon: 'RocketLaunchIcon', color: 'text-success' },
              { label: 'Total de likes', value: stats.totalLikes.toLocaleString('pt-PT'), icon: 'HeartIcon', color: 'text-danger' },
              { label: 'Total de jogadas', value: stats.totalPlays.toLocaleString('pt-PT'), icon: 'PlayIcon', color: 'text-accent' },
            ].map((stat) => (
              <div key={`profile-stat-${stat.label}`} className="text-center p-3 rounded-xl bg-muted/30">
                <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className={`${stat.color} mx-auto mb-1`} />
                <p className="text-xl font-bold text-foreground tabular-nums">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-bold text-foreground mb-4">Jogos publicados</h2>
          {games.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-2xl">
              <Icon name="RectangleStackIcon" size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Ainda não há jogos publicados</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {games.map((game) => (
                <Link
                  key={game.id}
                  href="/game-public-page"
                  className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-150 active:scale-[0.98]"
                >
                  <div className="relative aspect-video bg-muted overflow-hidden">
                    {game.thumbnail_url ? (
                      <img
                        src={game.thumbnail_url}
                        alt={game.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="RectangleStackIcon" size={32} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground mb-1 truncate">{game.title}</h3>
                    <p className="text-xs text-muted-foreground mb-3">{game.category}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="PlayIcon" size={12} className="text-primary" />
                        {game.plays_count.toLocaleString('pt-PT')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="HeartIcon" size={12} className="text-danger" />
                        {game.likes_count}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
